import React, { useState, useCallback, useMemo } from 'react';
import {
  Col, Container, Row, Stack, Spinner,
} from 'react-bootstrap';
import Button from 'components/DesignSystem/Button';
import Dialog from 'components/DesignSystem/Dialog';
import AboutTable from 'components/Admin/Flux/About/AboutTable';
import AboutMobile from 'components/Admin/Flux/About/Mobile';
import AboutModal from 'components/Admin/Flux/About/AboutModal';
import {
  useListFluxAboutDocumentsQuery,
  useDeleteFluxAboutDocumentMutation,
} from 'services/api/fluxAbout';
import { FluxAboutDocument, AboutSortField } from 'types/Flux/About/fluxAboutDocument';
import { SORT_ASC, SortDirection } from 'constants/sorting';
import useIsMobile from 'utils/hooks/useIsMobile';

function sortDocuments(
  docs: FluxAboutDocument[],
  sortBy: AboutSortField,
  sortDirection: SortDirection | undefined,
): FluxAboutDocument[] {
  if (!sortDirection) return docs;
  return [...docs].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];
    if (typeof aVal === 'string') {
      const cmp = aVal.localeCompare(bVal as string);
      return sortDirection === SORT_ASC ? cmp : -cmp;
    }
    const cmp = (aVal as number) - (bVal as number);
    return sortDirection === SORT_ASC ? cmp : -cmp;
  });
}

export default function AdminFluxAbout() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<
  FluxAboutDocument | null
  >(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<
  FluxAboutDocument | null
  >(null);

  const [sortBy, setSortBy] = useState<AboutSortField>('id');
  const [sortDirection, setSortDirection] = useState<
  SortDirection | undefined
  >(undefined);

  const { data, isLoading, isFetching } = useListFluxAboutDocumentsQuery();
  const [deleteDocument] = useDeleteFluxAboutDocumentMutation();
  const isMobile = useIsMobile();

  const sortedData = useMemo(
    () => (data ? sortDocuments(data, sortBy, sortDirection) : undefined),
    [data, sortBy, sortDirection],
  );

  const handleEdit = useCallback((doc: FluxAboutDocument) => {
    setEditingDocument(doc);
    setModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setEditingDocument(null);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingDocument(null);
  }, []);

  const handleDeleteRequest = useCallback((doc: FluxAboutDocument) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (documentToDelete) {
      await deleteDocument(documentToDelete.id);
    }
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  }, [documentToDelete, deleteDocument]);

  if (isLoading) {
    return <Spinner className="mt-5 mx-auto" animation="border" />;
  }

  return (
    <div>
      <AboutModal
        open={modalOpen}
        onClose={handleCloseModal}
        document={editingDocument}
      />
      <Dialog
        open={deleteDialogOpen}
        dismiss={() => setDeleteDialogOpen(false)}
        type="error"
      >
        <Dialog.Body>
          {`Tem certeza que deseja excluir o documento "${documentToDelete?.titulo}"?`}
        </Dialog.Body>
        <Dialog.Footer>
          <Button type="secondary" onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </Dialog.Footer>
      </Dialog>
      {isMobile ? (
        <AboutMobile
          documents={sortedData}
          isFetching={isFetching}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          onCreate={handleCreate}
        />
      ) : (
        <Container>
          <Row className="align-items-center pb-4">
            <Col>
              <Stack direction="horizontal" gap={3}>
                <h1 className="mb-0">DOCUMENTOS DA ÁREA SOBRE</h1>
              </Stack>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button icon="add" onClick={handleCreate}>
                Adicionar Documento
              </Button>
            </Col>
          </Row>
          <Row className="pt-5">
            <AboutTable
              documents={sortedData}
              isFetching={isFetching}
              onEdit={handleEdit}
              onDelete={handleDeleteRequest}
              sortBy={sortBy}
              sortDirection={sortDirection}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
            />
          </Row>
        </Container>
      )}
    </div>
  );
}
